import pluralize from 'pluralize';
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import { TransportItem, EnumTransportItemKind, OptionEndPoint, Transport } from 'src/types';
import Money from 'src/components/Money';
import classNames from 'classnames';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import TransportItemMap from 'src/components/option/TransportItemMap';

type Props = {
  isGiftExperience?: boolean;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  transportItems: TransportItem[];
  endPoints: OptionEndPoint[];
  transports?: Transport[];
};

const ModalTransportItems: React.FC<Props> = ({
  isGiftExperience,
  show,
  setShow,
  transportItems,
  endPoints,
  transports,
}) => {
  const currentCurrency = useCurrentCurrency();
  const [currentTransportItem, setCurrentTransportItem] = useState<TransportItem | undefined>(
    transportItems[0]
  );

  return (
    <Modal show={show} className="modal-styled">
      <Modal.Header>
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
          <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
            Where We’ll Meet
          </div>
          <FiX size={24} className="tw-cursor-pointer" onClick={() => setShow(false)} />
        </div>
      </Modal.Header>

      <Modal.Body>
        {currentTransportItem?.startPointLatitude ? (
          <div className="tw-h-72 tw-rounded-lg tw-border-2 tw-border-brand-green tw-relative tw-mb-5">
            <TransportItemMap
              key={currentTransportItem.id}
              transportItem={currentTransportItem}
              location={{
                latitude: currentTransportItem.startPointLatitude,
                longitude: currentTransportItem.startPointLongitude,
              }}
            />

            <div className="tw-absolute tw-bottom-6 tw-left-6 tw-bg-white tw-rounded-4xl tw-border-2 tw-border-brand-green tw-text-brand-navy tw-text-center tw-px-4 tw-py-2">
              {currentTransportItem.startPointDescription}
            </div>
          </div>
        ) : null}

        <div className="tw-font-semibold tw-font-styleSans tw-text-xl tw-mb-2">Start Point</div>
        <div
          className={classNames('tw-text-brand-navy tw-mb-4', {
            'tw-hidden': transportItems.length < 2,
          })}
        >
          Let your host know your preferred start point:
        </div>

        {transportItems.map((transportItem, index) => (
          <div key={transportItem.id} className={index > 0 ? 'tw-mt-4' : ''}>
            <div className="tw-text-brand-navy tw-mb-2">
              <span className="tw-font-semibold">
                {transportItem.kind === EnumTransportItemKind.meetup ? 'Meet-up' : 'Free pick-up'}
              </span>
              {transportItem.kind === EnumTransportItemKind.meetup ? ' at ' : ' from '}
              <span
                className={classNames('tw-underline tw-cursor-pointer', {
                  'tw-font-semibold': currentTransportItem?.id === transportItem.id,
                })}
                onClick={() => setCurrentTransportItem(transportItem)}
              >
                {transportItem.startPointDescription}
              </span>
              {transportItem.startPointAddress ? ` (${transportItem.startPointAddress})` : ''}
            </div>
            <ul className="tw-pl-12 tw-text-brand-navy tw-mb-0">
              {transportItem.startPointTime ? <li>{transportItem.startPointTime}</li> : null}
              {transportItem.note ? <li className="tw-mt-1">Notes: {transportItem.note}</li> : null}
            </ul>
          </div>
        ))}

        <div className="tw-my-6 tw-border-t twtw-text-brand-navy-lighter"></div>

        <div className="tw-font-semibold tw-font-styleSans tw-text-xl tw-mb-2">End Point</div>
        {endPoints.length > 0 ? (
          endPoints.map((endPoint, index) => (
            <div key={endPoint.id} className={`tw-text-grey ${index > 0 ? 'tw-mt-3' : ''}`}>
              <span className="tw-font-semibold">End at</span> {endPoint.description} (
              {endPoint.address})
            </div>
          ))
        ) : (
          <ul className="tw-text-brand-navy tw-pl-5 tw-mb-0">
            <li>End at Start Point(s)</li>
          </ul>
        )}

        {transports && transports.length > 0 ? (
          <>
            <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
            <div className="tw-font-semibold tw-mb-6 tw-text-xl">Transport Add-ons</div>
            {transports.map((transport, index) => (
              <div className={index > 0 ? 'tw-mt-6' : ''} key={transport.id}>
                <div className="tw-font-semibold tw-text-grey tw-leading-none tw-mb-2">
                  Add-on #{index + 1}
                </div>
                <ul className="tw-pl-5 tw-mb-0">
                  <li>
                    {transport.name}
                    (Price: <Money amount={transport.amount} toCurrency={currentCurrency} hasConversionFee={!isGiftExperience && transport.amount.currency !== currentCurrency} />
                    {transport.capacity
                      ? ` for up to ${pluralize('guest', transport.capacity, true)}`
                      : ''}
                    )
                  </li>
                </ul>
              </div>
            ))}
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default ModalTransportItems;
